/*#
# b3d-input-focus

Focus manager that wires input to the active controllable entity and handles
enter/exit vehicle mechanics.

## How It Works

1. Finds the `player: true` entity among its children, and wires keyboard, mouse,
   gamepads and XR controllers to it (through a `gameController` it builds, or
   one you nested to set its options)
2. Routes the controller's input to whichever entity has focus
3. On E press near an `enterable` vehicle, switches focus (hides biped, drives vehicle)
4. On E press while in a vehicle, exits back to biped

When switching entities, the input mapping is swapped to match the entity type
(e.g. bipedMapping for characters, aircraftMapping for aircraft).

## Attributes

| Attribute | Default | Description |
|-----------|---------|-------------|
| `enterDistance` | `3` | Max distance to enter a vehicle |

## Usage

```javascript
import { assetUrl, b3d, b3dBiped, b3dCar, b3dLibrary, inputFocus } from 'tosijs-3d'

document.body.append(
  b3d({},
    b3dLibrary({ url: assetUrl('tonio/vehicles/cars.glb'), type: 'cars' }),
    inputFocus(
      b3dBiped({ url: '/character.glb', player: true, cameraType: 'follow' }),
      b3dCar({ library: 'cars', meshName: 'Sedan', scale: 2, enterable: true, x: 5 })
    )
  )
)
```
*/
/*{ "parent": "input", "order": 20 }*/

import * as BABYLON from '@babylonjs/core'
import { B3dChild } from './b3d-utils.js'
import type { B3d } from './tosi-b3d.js'
import { B3dControllable } from './b3d-controllable.js'
import { useNearest } from './interactive-behavior.js'
import { gameController, type GameController } from './game-controller.js'
import {
  MappedInputProvider,
  bipedMapping,
  type GamepadSource,
} from './virtual-gamepad.js'
import { CompositeInputProvider } from './control-input.js'

export class B3dInputFocus extends B3dChild {
  static preferredTagName = 'tosi-b3d-input-focus'

  static initAttributes = {
    enterDistance: 3,
  }

  owner: B3d | null = null
  private focusedEntity: B3dControllable | null = null
  /** The currently controlled entity (biped/vehicle), or null. */
  get focused(): B3dControllable | null {
    return this.focusedEntity
  }
  private playerEntity: B3dControllable | null = null
  private gameController: GameController | null = null
  /** The current MappedInputProvider (exposed for late-binding by controllables) */
  inputMappedProvider: MappedInputProvider | null = null
  private interactWasPressed = false

  sceneReady(owner: B3d, _scene: BABYLON.Scene) {
    this.owner = owner

    /*
    A GameController, WHETHER OR NOT ANYONE SUPPLIED ONE.

    Without a provider `focusEntity` is never called, the player entity never
    gets an `inputProvider`, and nothing responds to anything — no keyboard, no
    hardware pad, and no glass pad, because the glass pad is a SOURCE added to
    this provider rather than an input path of its own. The element looked
    entirely correct: the biped was found, `player` was true, the pad was
    mounted and drawn, and `focusedEntity` was quietly null.

    That is what "it doesn't seem to be wired up at all" was, and it had already
    shipped in two demos written as `inputFocus(hero)`, which is the obvious
    spelling and the one a reader will copy. There is no configuration where an
    input manager with no input source is what someone meant, so the default is
    to build one rather than to do nothing.
    */
    let gcEl = this.querySelector('tosi-game-controller')
    if (gcEl == null) {
      gcEl = gameController()
      // Appended to the element itself, not through a slot: this is plumbing
      // the author did not ask for and should not have to see among their
      // children. Same shape as `b3d`'s own `_setupGamepad`.
      this.append(gcEl)
    }
    this.gameController = gcEl as unknown as GameController
    this.inputMappedProvider =
      this.gameController.getInputProvider(bipedMapping)

    // Defer discovery to ensure all children have completed sceneReady
    // (inputFocus is notified before its children in document order)
    requestAnimationFrame(() => this.discoverEntities())
  }

  /**
   * The live `player: true` controllable among our children — skipping any that are dead or
   * crashed, so a wreck lying on the hillside never gets picked as the player again.
   */
  private findPlayer(): B3dControllable | null {
    const controllables = Array.from(this.querySelectorAll('*')).filter(
      (el) => el instanceof B3dControllable
    ) as unknown as B3dControllable[]
    for (const entity of controllables) {
      const e = entity as any
      if (e.player && !e.crashed && !e.dead) return entity
    }
    return null
  }

  /**
   * A controllable calls this on ITS `sceneReady` — "I'm here, take me if you have nobody."
   *
   * This is how a RESPAWNED entity gets driven. The manager scans for `player: true` once,
   * at its own setup, so an aircraft appended later is invisible to it — and a caller that
   * appends one and immediately asks for a re-scan finds `player` still false, because
   * tosijs drains attributes on connectedCallback. Pull, don't push: by the child's
   * sceneReady the attributes are drained and the question can be answered truthfully.
   *
   * Only fills a VACANCY, so it can never steal the camera from a live player.
   */
  adoptIfVacant(entity: B3dControllable): void {
    // A DISCONNECTED focused entity is a vacant seat (board #2421): the belt
    // to the controllable's own release-on-dispose braces.
    if (this.focusedEntity != null && !this.focusedEntity.isConnected)
      this.releaseFocus()
    if (this.focusedEntity != null) return
    const e = entity as any
    if (!e.player || e.crashed || e.dead) return
    this.playerEntity = entity
    if (this.inputMappedProvider) this.focusEntity(entity)
  }

  /**
   * Re-scan for the player entity and drive it. Mostly unnecessary now — a controllable
   * announces itself via `adoptIfVacant` when it's ready — but useful to force a switch
   * (hand control to a different entity that's already in the scene).
   */
  focusPlayer(): B3dControllable | null {
    const player = this.findPlayer()
    if (player == null) return null
    this.playerEntity = player
    if (this.inputMappedProvider) this.focusEntity(player)
    return player
  }

  private discoverEntities() {
    this.playerEntity = this.findPlayer()

    if (this.playerEntity && this.inputMappedProvider) {
      this.focusEntity(this.playerEntity)
    }

    // Feed the scene's glass gamepad (if any) into the merged input — it's a
    // GamepadSource sitting alongside keyboard/hardware. Found by tag so it
    // works whether mounted by b3d's `gamepad` attribute or placed directly.
    const gamepad = this.owner?.querySelector(
      'tosi-b3d-gamepad'
    ) as unknown as GamepadSource | null
    if (gamepad != null && this.inputMappedProvider) {
      this.inputMappedProvider.addSource(gamepad)
    }

    // Register update loop for interact proximity check
    if (this.owner) {
      this.owner.scene.registerBeforeRender(this._checkInteract)
    }
  }

  focusEntity(entity: B3dControllable) {
    if (this.focusedEntity === entity) return

    // Deactivate old focus
    if (this.focusedEntity) {
      this.focusedEntity.handleLoseFocus()
      this.focusedEntity.inputProvider = null
    }

    this.focusedEntity = entity

    // Swap input mapping and wrap in CompositeInputProvider so XR etc. can add sources
    if (this.inputMappedProvider) {
      const mapping = entity.inputMapping ?? bipedMapping
      this.inputMappedProvider.setMapping(mapping)
      entity.inputProvider = new CompositeInputProvider(
        this.inputMappedProvider
      )
    }
    entity.handleGainFocus()

    // Switch camera to follow the new entity
    this.setupCameraForEntity(entity)
  }

  /**
   * Stop driving whatever we were driving. **Death needs an exit.**
   *
   * Without this the manager stays welded to a wrecked entity: the player goes on "flying"
   * a corpse, the game has no way out, and the whole loop dead-ends. (`b3d-aircraft` even
   * said so — on crash: "Stays put until something resets it", and nothing did.) The
   * camera is left alone deliberately: whoever handles the death (`b3d-death`) wants to
   * take it over, and yanking it back to a default here would fight them.
   */
  releaseFocus(): void {
    const entity = this.focusedEntity
    if (entity == null) return
    entity.handleLoseFocus()
    entity.inputProvider = null
    this.focusedEntity = null
  }

  private setupCameraForEntity(entity: B3dControllable) {
    // For bipeds, let them handle their own camera via cameraType attribute
    // For other entities, set up a follow camera on the entity's camera target
    if ('setupFollowCamera' in entity) {
      /*
      Self-managed entity (aircraft, biped) — but "it manages its own camera"
      had an ordering HOLE (manta-recon, issue #1): the aircraft defers camera
      creation until both mesh AND inputProvider exist, so when focus adoption
      lands AFTER a fast mesh load, its setupMesh saw inputProvider == null and
      skipped — and we early-returned. Nobody made the camera; the player flew
      blind on default-camera. Nudge entities that use the deferral pattern
      (they expose `chaseCamera`); setupFollowCamera is idempotent (guards its
      own chaseCamera / missing target), so calling from both sides is safe and
      whichever side runs LAST completes the setup. Bipeds are left alone —
      their setupFollowCamera has XR side effects and runs off the player
      attribute, not focus adoption.
      */
      const e = entity as unknown as {
        chaseCamera?: unknown
        getCameraTarget: () => unknown
        setupFollowCamera: () => void
      }
      if ('chaseCamera' in e && e.chaseCamera == null && e.getCameraTarget()) {
        e.setupFollowCamera()
      }
      return
    }
    if (!this.owner) return
    const target = entity.getCameraTarget()
    if (!target) return

    const followCamera = new BABYLON.FollowCamera(
      'FollowCam',
      BABYLON.Vector3.Zero(),
      this.owner.scene
    )
    followCamera.radius = 8
    followCamera.heightOffset = 3
    followCamera.rotationOffset = 180
    followCamera.lockedTarget = target as BABYLON.AbstractMesh
    this.owner.setActiveCamera(followCamera, { attach: false })
  }

  private _checkInteract = () => {
    if (!this.inputMappedProvider || !this.playerEntity || !this.owner) return

    const input = this.inputMappedProvider.poll(0)
    const interactPressed = input.interact > 0.5
    const justPressed = interactPressed && !this.interactWasPressed
    this.interactWasPressed = interactPressed

    if (!justPressed) return

    if (this.focusedEntity === this.playerEntity) {
      // Player is on foot — check for nearby enterable entities
      const playerTarget = this.playerEntity.getCameraTarget()
      if (!playerTarget) return
      const playerPos =
        (playerTarget as BABYLON.AbstractMesh).absolutePosition ??
        (playerTarget as BABYLON.TransformNode).position

      const enterDist = (this as any).enterDistance as number

      // Find all controllable entities with enterable=true
      const candidates = Array.from(this.querySelectorAll('*')).filter((el) => {
        if (!(el instanceof B3dControllable)) return false
        if (el === this.playerEntity) return false
        return (el as any).enterable === true
      }) as unknown as B3dControllable[]

      let closest: B3dControllable | null = null
      let closestDist = enterDist

      for (const candidate of candidates) {
        const target = candidate.getCameraTarget()
        if (!target) continue
        const pos =
          (target as BABYLON.AbstractMesh).absolutePosition ??
          (target as BABYLON.TransformNode).position
        const dist = BABYLON.Vector3.Distance(playerPos, pos)
        if (dist < closestDist) {
          closestDist = dist
          closest = candidate
        }
      }

      if (closest) {
        this.enterVehicle(closest)
        return
      }

      /*
      NOTHING TO GET INTO — SO REACH FOR WHAT IS THERE.

      `interact` is one verb and it had exactly one meaning: get in a vehicle.
      Which left `b3d-interactive` — doors, knobs, switches, levers, the entire
      "touch a mesh" substrate — reachable ONLY by mouse pointer. `useNearest`,
      the function whose whole job is to be bound to this button, had no caller
      anywhere in the engine; it was written, exported, documented with this
      exact line in `b3d-interactive`'s own page, and never wired.

      On a keyboard or a pad that is not a hard-to-reach control, it is no
      control at all: you walk up to a lift switch, press the button the game
      told you to press, and nothing happens — with no hover, no refusal and no
      error, because you never generated a pointer event in the first place.
      Tonio, twice: "I can't figure out how to activate the red elevator."

      Vehicles keep priority. Standing beside a car with a door panel on the
      wall, the car is what you meant — and the interactive is still there when
      you step away from it.
      */
      useNearest(this.owner.scene, playerPos as BABYLON.Vector3)
    } else {
      // Player is in a vehicle — exit back to biped
      this.exitVehicle()
    }
  }

  private enterVehicle(vehicle: B3dControllable) {
    if (!this.playerEntity) return

    // Hide the biped mesh
    const playerTarget = this.playerEntity.getCameraTarget()
    if (playerTarget) {
      ;(playerTarget as BABYLON.AbstractMesh).setEnabled?.(false)
    }

    // Focus on the vehicle
    this.focusEntity(vehicle)
  }

  private exitVehicle() {
    if (!this.playerEntity || !this.focusedEntity) return
    const vehicle = this.focusedEntity

    // Find exit point or vehicle position
    const vehicleTarget = vehicle.getCameraTarget()
    const exitPos = vehicleTarget
      ? (
          (vehicleTarget as BABYLON.AbstractMesh).absolutePosition ??
          (vehicleTarget as BABYLON.TransformNode).position
        ).clone()
      : new BABYLON.Vector3(0, 0, 0)

    // Offset the biped to the side of the vehicle
    exitPos.x += 2

    // Show and reposition the biped
    const playerTarget = this.playerEntity.getCameraTarget()
    if (playerTarget) {
      ;(playerTarget as BABYLON.AbstractMesh).setEnabled?.(true)
      ;(playerTarget as BABYLON.TransformNode).position.copyFrom(exitPos)
    }

    // Focus back on the biped
    this.focusEntity(this.playerEntity)
  }

  sceneDispose() {
    if (this.owner) {
      this.owner.scene.unregisterBeforeRender(this._checkInteract)
    }
    this.focusedEntity = null
    this.playerEntity = null
    this.gameController = null
    this.inputMappedProvider = null
    this.owner = null
  }
}

export const inputFocus = B3dInputFocus.elementCreator()
